import { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import ProductDropdown from './ProductDropdown'

const PublicNav = ({ onShowFeatures, onShowPricing, onLogin, onBack, activeProduct = false, activePricing = false, activeHome = false }) => {
  const basePath = import.meta.env.BASE_URL
  const [isProductOpen, setIsProductOpen] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const dropdownRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProductOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleFeatureClick = (sectionId) => {
    onShowFeatures()
    setIsProductOpen(false)
    setTimeout(() => {
      const element = document.getElementById(sectionId)
      if (element) {
        const offset = sectionId.startsWith('assistant-') && sectionId !== 'assistant-auri' ? 90 : 0
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset
        const offsetPosition = elementPosition - offset
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' })
        
        element.classList.add('animate-flash')
        setTimeout(() => {
          element.classList.remove('animate-flash')
        }, 2000)
      }
    }, 100)
  }

  const handleProductTourClick = () => {
    setIsProductOpen(false)
    onShowFeatures()
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100)
  }

  return (
    <nav className="bg-transparent relative z-[9999] border-b border-white/10">
      <div className="mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between px-4 sm:px-0">
          <div className="flex items-center">
            <button 
              onClick={onBack}
              className="shrink-0 flex items-center"
            >
              <img src={`${basePath}images/logo.png`} alt="Opptora Logo" className="size-8" />
              <span className="text-white font-semibold text-2xl ml-2">Opptora</span>
            </button>
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center">
              <div className="relative ml-6" ref={dropdownRef} onMouseEnter={() => window.innerWidth >= 1024 && setIsProductOpen(true)} onMouseLeave={() => window.innerWidth >= 1024 && setIsProductOpen(false)}>
                <button 
                  onClick={() => setIsProductOpen(!isProductOpen)}
                  className={`rounded-md px-3 py-2 text-base font-display font-light tracking-tight flex items-center gap-1 ${
                    activeProduct ? 'bg-sky-500/20 text-sky-300' : 'bg-gray-900 text-white hover:bg-gray-800'
                  }`}
                >
                  Product
                  <svg className={`w-4 h-4 transition-transform ${isProductOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isProductOpen && (
                  <ProductDropdown 
                    onFeatureClick={handleFeatureClick}
                    onProductTourClick={handleProductTourClick}
                    onClose={() => setIsProductOpen(false)}
                  />
                )}
              </div>
              <button 
                onClick={onShowPricing}
                className={`ml-6 rounded-md px-3 py-2 text-base font-display font-light tracking-tight ${
                  activePricing ? 'bg-sky-500/20 text-sky-300' : 'bg-gray-900 text-white hover:bg-gray-800'
                }`}
              >
                Pricing
              </button>
            </div>
          </div>
          {/* Desktop Auth Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <button 
              onClick={() => onLogin('login')}
              className="rounded-md bg-gray-900 px-4 py-2 text-base font-medium text-white hover:bg-gray-800"
            >
              Sign In
            </button>
            <button 
              onClick={() => onLogin('register')}
              className="rounded-md bg-gradient-to-br from-blue-500/60 to-blue-600/40 px-4 py-2 text-base font-semibold text-white hover:from-blue-500/50 hover:to-blue-600/30"
            >
              Get Started
            </button>
          </div>
          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden rounded-md p-2 text-white hover:bg-gray-800"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden border-t border-white/10 bg-gray-950">
          <div className="px-4 py-3 space-y-3">
            <button
              onClick={() => {
                onBack()
                setIsMobileMenuOpen(false)
              }}
              className={`block w-full text-left px-3 py-2 rounded-md text-base font-display font-light tracking-tight ${
                activeHome ? 'bg-sky-500/20 text-sky-300' : 'bg-gray-900 text-white hover:bg-gray-800'
              }`}
            >
              Home
            </button>
            <button
              onClick={() => {
                onShowFeatures()
                setIsMobileMenuOpen(false)
              }}
              className={`block w-full text-left px-3 py-2 rounded-md text-base font-display font-light tracking-tight ${
                activeProduct ? 'bg-sky-500/20 text-sky-300' : 'bg-gray-900 text-white hover:bg-gray-800'
              }`}
            >
              Product
            </button>
            <button
              onClick={() => {
                onShowPricing()
                setIsMobileMenuOpen(false)
              }}
              className={`block w-full text-left px-3 py-2 rounded-md text-base font-display font-light tracking-tight ${
                activePricing ? 'bg-sky-500/20 text-sky-300' : 'bg-gray-900 text-white hover:bg-gray-800'
              }`}
            >
              Pricing
            </button>
            <div className="border-t border-white/10 pt-3 space-y-3">
              <button
                onClick={() => {
                  onLogin('login')
                  setIsMobileMenuOpen(false)
                }}
                className="block w-full rounded-md bg-gray-900 px-4 py-2 text-base font-medium text-white hover:bg-gray-800"
              >
                Sign In
              </button>
              <button
                onClick={() => {
                  onLogin('register')
                  setIsMobileMenuOpen(false)
                }}
                className="block w-full rounded-md bg-gradient-to-br from-blue-500/60 to-blue-600/40 px-4 py-2 text-base font-semibold text-white hover:from-blue-500/50 hover:to-blue-600/30"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  )
}

PublicNav.propTypes = {
  onShowFeatures: PropTypes.func.isRequired,
  onShowPricing: PropTypes.func.isRequired,
  onLogin: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  activeProduct: PropTypes.bool,
  activePricing: PropTypes.bool,
  activeHome: PropTypes.bool,
}

export default PublicNav
